/**
 * Co-located eq factory for AccountFirmsRpc (`@cova/shell/account-firms`,
 * served by the identity context at `ACCOUNT_FIRMS_RPC_PATH`): the signed-in
 * person's firms in this region that resolve the platform, for the firm
 * switcher (WP3.15.7). Account-level: no firm header is needed, the person is
 * request-derived server-side.
 */
import { Effect, Layer } from "effect"
import { RpcClient, RpcSerialization } from "@effect/rpc"

import { ACCOUNT_FIRMS_RPC_PATH, AccountFirmsRpc } from "@cova/shell/account-firms"
import { createEffectQueryNoAbort } from "@/lib/api/effect-query-utils"
import { AppHttpClient } from "@/shared/scope"

const AccountFirmsRpcLive = RpcClient.layerProtocolHttp({ url: ACCOUNT_FIRMS_RPC_PATH }).pipe(
  Layer.provide(AppHttpClient.layer),
  Layer.provideMerge(RpcSerialization.layerJson),
)

export const accountFirmsEq = createEffectQueryNoAbort(AccountFirmsRpcLive)

export const accountFirmsKeys = {
  /** Keyed by person: a tab that signs in as someone else never reads the previous person's firms. */
  platform: (userId: string | null) => ["account-firms", "platform", userId] as const,
  switchable: (userId: string | null) => ["account-firms", "switchable", userId] as const,
}

/** The person's firms that resolve the platform now, each under its acting scope. */
export const platformFirmsQuery = (userId: string | null) =>
  accountFirmsEq.queryOptions({
    queryKey: accountFirmsKeys.platform(userId),
    queryFn: () =>
      Effect.gen(function* () {
        const client = yield* RpcClient.make(AccountFirmsRpc)
        return yield* client.listMyFirms({ app: "platform", lastUsedOrgId: null })
      }).pipe(Effect.scoped),
    enabled: userId !== null,
    // The answer is live and app-filtered (app-access §5.8): kept within the
    // resolver's own 30-second bound, so an app turned off drops the firm soon.
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: 1,
  })

/**
 * Every firm the person is in (`allFirms`), for the firm switcher. Not only the
 * firms whose seat resolves the platform: an unpaid firm, or one whose seat does
 * not resolve the platform, must still be offered, or a staffer in /cs
 * has no way back to their broker firm. The switcher keeps the firms whose
 * scope works in the platform app (`platformSwitchableFirmsOf`); the firm
 * opened re-admits the person live and shows its own billing wall. App-blind,
 * so the server makes no resolution for it and it keeps its 5-minute cache.
 */
export const switchableFirmsQuery = (userId: string | null) =>
  accountFirmsEq.queryOptions({
    queryKey: accountFirmsKeys.switchable(userId),
    queryFn: () =>
      Effect.gen(function* () {
        const client = yield* RpcClient.make(AccountFirmsRpc)
        return yield* client.listMyFirms({ app: "platform", lastUsedOrgId: null, allFirms: true })
      }).pipe(Effect.scoped),
    enabled: userId !== null,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  })
